import React from 'react'
import { Icon } from '../../../../atoms/Icon'
import { Loading } from '../../../../atoms/Loading'
import { Card, Grid } from '../../BuildingCharacteristics.styles'
import type { NeighborhoodContextTabProps } from './NeighborhoodContextTab.types'
import { useNeighborhoodAnalysis, useComparisonText } from './hooks'
import {
  calculateBuildingConformity,
  extractBuildingMetrics,
  extractNeighborMetrics,
  hasNeighborData,
} from './utils'
import {
  NoDataCard,
  RadiusSelector,
  ComparisonChartsSection,
  ComparativeAnalysisCard,
  NeighborhoodStatsCard,
  NeighborhoodAnalysisCard,
  CohesionBreakdownCard,
  ConformityBreakdownCard,
  QuartierProfileCard,
  AgeDistributionChart,
  NeighborhoodStatisticsGrid,
} from './components'

export const NeighborhoodContextTab: React.FC<NeighborhoodContextTabProps> = ({
  building,
  t,
  onFetchNeighborhoodAnalysis,
  initialNeighborhoodAnalysis,
  onNeighborhoodAnalysisChange,
}) => {
  const {
    neighborhoodAnalysis,
    isLoadingAnalysis,
    analysisError,
    selectedRadius,
    setSelectedRadius,
    setHasUserChangedRadius,
  } = useNeighborhoodAnalysis({
    building,
    initialNeighborhoodAnalysis,
    onFetchNeighborhoodAnalysis,
    onNeighborhoodAnalysisChange,
    t,
  })

  const { getComparisonText } = useComparisonText(t)

  const buildingConformity = calculateBuildingConformity(
    neighborhoodAnalysis,
    building
  )

  const buildingMetrics = extractBuildingMetrics(building)
  const neighborMetrics = extractNeighborMetrics(building)
  const hasNeighbors = hasNeighborData(neighborMetrics)

  // Show "no data" message only if both old and new data are missing
  if (
    !hasNeighbors &&
    !neighborhoodAnalysis &&
    !isLoadingAnalysis &&
    !analysisError
  ) {
    return <NoDataCard t={t} />
  }

  const handleRadiusChange = (radius: number) => {
    setHasUserChangedRadius(true)
    setSelectedRadius(radius)
  }

  return (
    <>
      {/* Comparative Charts - Only show if old neighbor data exists */}
      {hasNeighbors && (
        <>
          <ComparisonChartsSection
            buildingHeight={buildingMetrics.buildingHeight}
            buildingArea={buildingMetrics.buildingArea}
            buildingVolume={buildingMetrics.buildingVolume}
            neighborsHeightMean={neighborMetrics.neighborsHeightMean}
            neighborsHeightMax={neighborMetrics.neighborsHeightMax}
            neighborsAreaMean={neighborMetrics.neighborsAreaMean}
            neighborsAreaMax={neighborMetrics.neighborsAreaMax}
            neighborsVolumeMean={neighborMetrics.neighborsVolumeMean}
            neighborsVolumeMax={neighborMetrics.neighborsVolumeMax}
            neighborsCount={neighborMetrics.neighborsCount}
            t={t}
          />

          {/* Detailed Comparison Metrics */}
          <Grid $columns={2}>
            <ComparativeAnalysisCard
              buildingHeight={buildingMetrics.buildingHeight}
              buildingArea={buildingMetrics.buildingArea}
              buildingVolume={buildingMetrics.buildingVolume}
              neighborsHeightMean={neighborMetrics.neighborsHeightMean}
              neighborsAreaMean={neighborMetrics.neighborsAreaMean}
              neighborsVolumeMean={neighborMetrics.neighborsVolumeMean}
              getComparisonText={getComparisonText}
              t={t}
            />

            <NeighborhoodStatsCard
              building={building}
              neighborsCount={neighborMetrics.neighborsCount}
              t={t}
            />
          </Grid>
        </>
      )}

      {/* Quartier Analysis Section */}
      {isLoadingAnalysis && (
        <Card>
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <Loading />
            <div style={{ marginTop: '16px', color: '#666', fontSize: '14px' }}>
              {t('neighborhood.loading')}
            </div>
          </div>
        </Card>
      )}

      {analysisError && !isLoadingAnalysis && (
        <Card>
          <div
            style={{
              padding: '40px',
              textAlign: 'center',
              color: '#e74c3c',
              fontSize: '14px',
            }}
          >
            <Icon name="alert-circle" size="medium" />
            <div style={{ marginTop: '12px' }}>{analysisError}</div>
          </div>
        </Card>
      )}

      {neighborhoodAnalysis && !isLoadingAnalysis && (
        <>
          {/* Radius Selector */}
          <RadiusSelector
            selectedRadius={selectedRadius}
            onRadiusChange={handleRadiusChange}
            t={t}
          />

          {/* Neighborhood & Building Analysis - Full Width */}
          <NeighborhoodAnalysisCard
            neighborhoodAnalysis={neighborhoodAnalysis}
            buildingConformity={buildingConformity}
            t={t}
          />

          <div style={{ marginBottom: 24 }} />

          {/* Cohesion Breakdown and Building Conformity Breakdown - Side by Side */}
          <Grid $columns={2}>
            <CohesionBreakdownCard
              neighborhoodAnalysis={neighborhoodAnalysis}
              t={t}
            />

            {/* Building Conformity Breakdown */}
            {buildingConformity && (
              <ConformityBreakdownCard
                buildingConformity={buildingConformity}
                t={t}
              />
            )}
          </Grid>

          {/* Quartier Profile */}
          <QuartierProfileCard
            neighborhoodAnalysis={neighborhoodAnalysis}
            t={t}
          />

          <div style={{ marginBottom: 24 }} />

          {/* Age Distribution Chart */}
          <AgeDistributionChart
            neighborhoodAnalysis={neighborhoodAnalysis}
            t={t}
          />

          <div style={{ marginBottom: 24 }} />

          {/* Neighborhood Statistics */}
          <NeighborhoodStatisticsGrid
            neighborhoodAnalysis={neighborhoodAnalysis}
            t={t}
          />
        </>
      )}
    </>
  )
}
